import { z } from 'zod';

/**
 * Deterministic placement of an AI generator's batch-relative output on the
 * global 0-100 priority scale: the model's raw priority is relative importance
 * within its own batch, linearly rescaled into [min, max] by the insights
 * runner (packages/insights/src/priority.ts). Lives in @graftus/schema so the
 * admin dashboard can edit overrides without depending on @graftus/insights.
 */
export const PriorityBand = z
  .object({
    /** 0-100 */
    min: z.number().int().min(0).max(100),
    /** 0-100, >= min */
    max: z.number().int().min(0).max(100),
  })
  .refine((band) => band.max >= band.min, {
    message: 'max must be >= min',
  });
export type PriorityBand = z.infer<typeof PriorityBand>;

/**
 * Code defaults for every band-capable generator - the single source of truth
 * the generator objects import their `priorityBand` from. Admins can override
 * a band platform-wide from the admin dashboard (stored in the
 * `PlatformConfig/insightPriorityBands` doc); an absent override means the
 * default below applies.
 *
 * The team-dynamics ceiling of 85 is deliberately the highest in the system
 * (external signals 80, local webapp nudges 76, follow-ups 60, pulse rule
 * constants <= 58): that generator carries the KPI block, and KPI-gap
 * insights must be able to outrank every other surface.
 *
 * Generators absent from this catalog are never remapped: rule generators
 * emit calibrated code constants, ai-experiment-verdict prompt-steers a fixed
 * 45-60 range, and ai-member-performance runs outside
 * `runGeneratorsForBusiness` (its priorities are prompt-steered).
 */
export const INSIGHT_PRIORITY_BAND_DEFAULTS = {
  'ai-team-dynamics': { min: 40, max: 85 },
  'ai-external-signals': { min: 40, max: 80 },
  'ai-action-follow-up': { min: 40, max: 60 },
} as const satisfies Record<string, PriorityBand>;

export type BandedGeneratorKey = keyof typeof INSIGHT_PRIORITY_BAND_DEFAULTS;

export const BANDED_GENERATOR_KEYS = Object.keys(
  INSIGHT_PRIORITY_BAND_DEFAULTS,
) as BandedGeneratorKey[];

export function isBandedGeneratorKey(key: string): key is BandedGeneratorKey {
  return key in INSIGHT_PRIORITY_BAND_DEFAULTS;
}
